import {
  Box, Grid, TextField, Typography, Collapse, IconButton, Tooltip,
  Chip, Paper, FormControl, InputLabel, Select, MenuItem,
} from "@mui/material";
import {
  ExpandMore, ExpandLess, AllInclusive,
  CalendarToday, Tune,
} from "@mui/icons-material";
import { useState } from "react";
import { COLORS } from "../theme";
import { LIMIT_FIELDS } from "../utils/helpers";

// ── Section wrapper ───────────────────────────────────────────────────────────
function Section({ id, icon, label, badge, open, onToggle, children }) {
  return (
    <Paper sx={{ borderRadius: 2.5, border: `1px solid ${COLORS.border}`, mb: 2, overflow: "hidden", boxShadow: "none" }}>
      <Box
        onClick={onToggle}
        sx={{
          display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 1.8,
          cursor: "pointer", bgcolor: open ? COLORS.accentLight : COLORS.surface,
          borderBottom: open ? `1px solid ${COLORS.border}` : "none",
          transition: "background 0.15s",
        }}
      >
        <Box sx={{ color: open ? COLORS.accent : COLORS.muted, display: "flex" }}>{icon}</Box>
        <Typography sx={{ fontWeight: 700, fontSize: 13, flex: 1, color: open ? COLORS.accent : COLORS.ink }}>
          {label}
        </Typography>
        {badge !== undefined && (
          <Chip size="small" label={badge} sx={{ fontSize: 11, height: 20, bgcolor: open ? "#c7d2fe" : COLORS.border, color: open ? COLORS.accent : COLORS.muted }} />
        )}
        {open ? <ExpandLess sx={{ fontSize: 18, color: COLORS.muted }} /> : <ExpandMore sx={{ fontSize: 18, color: COLORS.muted }} />}
      </Box>
      <Collapse in={open}>
        <Box sx={{ p: 2.5 }}>{children}</Box>
      </Collapse>
    </Paper>
  );
}

// ── Limit row: input + unlimited toggle ───────────────────────────────────────
// unlimitedExclude: array of field keys (e.g. ["aiquery"]) whose unlimited toggle is hidden.
function LimitRow({ field, form, onChange, unlimitedExclude = [] }) {
  const isExcluded = unlimitedExclude.includes(field.key);
  const unlimited = !isExcluded && field.unlimitedKey ? !!form[field.unlimitedKey] : false;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
      <Box sx={{ fontSize: 16, width: 24, textAlign: "center", flexShrink: 0 }}>{field.icon}</Box>
      <TextField
        size="small"
        label={field.label}
        type="number"
        fullWidth
        value={unlimited ? "" : (form[field.key] ?? "")}
        placeholder={unlimited ? "Unlimited" : ""}
        disabled={unlimited}
        onChange={e => onChange(field.key, e.target.value === "" ? "" : Number(e.target.value))}
        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
        InputProps={{
          inputProps: { min: 0 },
          endAdornment: unlimited && (
            <Chip size="small" label="∞" sx={{ fontSize: 11, height: 20, bgcolor: COLORS.accentLight, color: COLORS.accent, mr: 0.5 }} />
          ),
        }}
      />
      {field.unlimitedKey && !isExcluded && (
        <Tooltip title={unlimited ? "Set limit" : "Make unlimited"} arrow>
          <IconButton
            size="small"
            onClick={() => onChange(field.unlimitedKey, !unlimited)}
            sx={{
              flexShrink: 0,
              color: unlimited ? COLORS.accent : COLORS.textSub,
              border: `1px solid ${unlimited ? COLORS.accent : COLORS.border}`,
              borderRadius: 1.5,
              p: "5px",
              bgcolor: unlimited ? COLORS.accentLight : "transparent",
              "&:hover": { bgcolor: COLORS.accentLight, borderColor: COLORS.accent, color: COLORS.accent },
            }}
          >
            <AllInclusive sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

// ── Main form ─────────────────────────────────────────────────────────────────
// unlimitedExclude: array of field keys whose "make unlimited" toggle is hidden,
// e.g. ["aiquery"] to remove the unlimited option for AI queries.
export default function UsageForm({
  form,
  onChange,
  isMultiple = false,
  disabledFields = [],
  showStatus = true,
  unlimitedExclude = [],
}) {
  const [open, setOpen] = useState({ plan: true, limits: true });
  const toggle = k => setOpen(p => ({ ...p, [k]: !p[k] }));

  const isDisabled = (key) => disabledFields.includes(key);

  // Don't count excluded fields toward the unlimited badge.
  const unlimitedCount = LIMIT_FIELDS.filter(
    f => f.unlimitedKey && !unlimitedExclude.includes(f.key) && form[f.unlimitedKey]
  ).length;

  // In multiple-record mode the end date is derived from Start Date + Plan Interval,
  // so the End Date input is hidden — it's not editable input there.
  const hideEndDate = isMultiple;

  // End date must be strictly after start date (only relevant when End Date is shown).
  const endDateInvalid = !hideEndDate && !!(form.startdate && form.enddate && form.enddate <= form.startdate);

  return (
    <Box>
      {/* ── Plan & Dates ── */}
      <Section id="plan" icon={<CalendarToday sx={{ fontSize: 18 }} />} label="Plan & Dates" open={open.plan} onToggle={() => toggle("plan")}>
        <Grid container spacing={2}>
          {/* Business ID (read-only display) */}
          {form.businessid && (
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Business ID"
                value={form.businessid} disabled
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, bgcolor: COLORS.surface } }}
              />
            </Grid>
          )}

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" label="Start Date *" type="date"
              value={form.startdate || ""}
              onChange={e => onChange("startdate", e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
            />
          </Grid>

          {!hideEndDate && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="End Date *" type="date"
                value={form.enddate || ""}
                onChange={e => onChange("enddate", e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={endDateInvalid}
                helperText={endDateInvalid ? "End date must be after start date" : ""}
                inputProps={{ min: form.startdate || undefined }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />
            </Grid>
          )}

          {isMultiple && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Plan Interval (months) *" type="number"
                value={form.planinterval ?? ""}
                onChange={e => {
                  const raw = e.target.value;
                  onChange("planinterval", raw === "" ? "" : Math.max(1, Number(raw)));
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                inputProps={{ min: 1, step: 1 }}
                helperText="Minimum 1 month — end date is generated automatically from this"
              />
            </Grid>
          )}

          {showStatus && (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={form.isactive ? "active" : "inactive"}
                  label="Status"
                  onChange={e => onChange("isactive", e.target.value === "active")}
                >
                  <MenuItem value="active">✅ Active</MenuItem>
                  <MenuItem value="inactive">❌ Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </Section>

      {/* ── Usage Limits ── */}
      <Section
        id="limits"
        icon={<Tune sx={{ fontSize: 18 }} />}
        label="Usage Limits"
        badge={unlimitedCount > 0 ? `${unlimitedCount} unlimited` : LIMIT_FIELDS.length}
        open={open.limits}
        onToggle={() => toggle("limits")}
      >
        <Grid container spacing={0}>
          {LIMIT_FIELDS.map((f) => (
            <Grid item xs={12} sm={6} key={f.key} sx={{ px: 0.5 }}>
              <LimitRow field={f} form={form} onChange={onChange} unlimitedExclude={unlimitedExclude} />
            </Grid>
          ))}
        </Grid>
      </Section>
    </Box>
  );
}